import type { DirectExecution } from '@funkit/api-base'
import {
  type PurifiedCheckoutHistoryItem,
  processRecentDepositsForNotifications,
  purifyCheckoutHistoryItem,
  useRecentDepositsApiQuery,
} from '@funkit/connect-core'
import { useMemo } from 'react'

import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { useDynamicConfig } from '../statsig/useDynamicConfig'

interface UseFunkitRecentDepositsParams {
  isVisible: boolean
  fetchSize?: number
  cutOffTimeMs?: number
  /**
   * Optional filter to refine which deposits are returned. Pass a stable
   * reference (`useCallback`) — an unstable one recomputes the merge each render.
   */
  memoizedFilterFunc?: (de: DirectExecution) => boolean
}

interface UseFunkitRecentDepositsImplParams extends UseFunkitRecentDepositsParams {
  apiKey: string
  userId: string
  refreshIntervalMs: number
}

/**
 * Fetch the current user's recent deposits, polling the funkit server. Mirrors
 * `@funkit/connect`'s `useFunkitRecentDeposits`.
 *
 * **Requirements**: must be used inside `<FunkitProvider>` — it reads the
 * apiKey/userId configured there and the shared refresh interval. The `'0x'`
 * sentinel (no wallet / no externalUserId) disables polling.
 *
 * @returns `{ data }` — purified deposit rows, or `undefined` while loading.
 *
 * @example
 * ```tsx
 * const { data: recentDeposits } = useFunkitRecentDeposits({
 *   isVisible: true,
 *   fetchSize: 10,
 *   cutOffTimeMs: 24 * 60 * 60 * 1000, // last 24h
 * })
 * ```
 */
export function useFunkitRecentDeposits(query: UseFunkitRecentDepositsParams): {
  data: PurifiedCheckoutHistoryItem[] | undefined
} {
  const { apiKey, userId } = useFunkitIdentity()
  const { listRefresh } = useDynamicConfig(
    'checkoutnotificationsrefreshinterval',
  )

  return useFunkitRecentDepositsImpl({
    ...query,
    apiKey,
    // The '0x' sentinel means no wallet / no externalUserId — disables polling.
    userId: userId === '0x' ? '' : userId,
    refreshIntervalMs: listRefresh,
  })
}

/**
 * Explicit-params variant of `useFunkitRecentDeposits`. Mirrors
 * `@funkit/connect`'s `useFunkitRecentDepositsImpl`.
 *
 * **Requirements**: does NOT require `<FunkitProvider>` — the host passes
 * identity and the refresh interval itself. Use it when polling outside the
 * provider hierarchy, or for a user other than the configured one.
 *
 * @returns `{ data }` — purified deposit rows, or `undefined` while loading.
 *
 * @example
 * ```tsx
 * const { data: recentDeposits } = useFunkitRecentDepositsImpl({
 *   isVisible: true,
 *   apiKey: 'your-api-key',
 *   userId: 'funkit|external-user-42',
 *   refreshIntervalMs: 5000,
 *   fetchSize: 10,
 * })
 * ```
 */
export function useFunkitRecentDepositsImpl({
  isVisible,
  fetchSize = 5,
  cutOffTimeMs,
  memoizedFilterFunc,
  apiKey,
  userId,
  refreshIntervalMs,
}: UseFunkitRecentDepositsImplParams): {
  data: PurifiedCheckoutHistoryItem[] | undefined
} {
  const { data: directExecutions } = useRecentDepositsApiQuery({
    isVisible,
    apiKey,
    userId,
    refreshIntervalMs,
    fetchSize: fetchSize * 2, // over-fetch to account for merging
    logger,
  })

  const mergedDirectExecutions = useMemo(
    () =>
      processRecentDepositsForNotifications(
        memoizedFilterFunc
          ? directExecutions?.filter(memoizedFilterFunc)
          : directExecutions,
        { cutOffTimeMs },
      ),
    [directExecutions, cutOffTimeMs, memoizedFilterFunc],
  )

  return useMemo(
    () => ({
      data: mergedDirectExecutions
        ?.map((de) => purifyCheckoutHistoryItem(de))
        .slice(0, fetchSize),
    }),
    [mergedDirectExecutions, fetchSize],
  )
}
